import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormProvider } from '@thinknimble/tn-forms-react'
import { ReportForm, TReportForm } from 'src/services/expense-reports/forms'
import { useTnForm } from '@thinknimble/tn-forms-react'
import { Modal } from '../modal'
import { Input } from '../input'
import { Button } from '../button'
import { ErrorsList } from '../errors'
import { expenseReportsApi } from 'src/services/expense-reports/api'
import { expenseReportQueries } from 'src/services/expense-reports/queries'
import { SelectWrapper } from '../select-wrapper'
import { CustomSelect } from '../custom-select'
import { organizationQueries } from 'src/services/user'
import { ExpenseReport } from 'src/services/expense-reports'

interface CreateExpenseReportModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (expenseReport: ExpenseReport) => void
  initialName?: string
}

const CreateExpenseReportModalContent: React.FC<CreateExpenseReportModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialName = '',
}) => {
  const { t } = useTranslation()
  const { form, createFormFieldChangeHandler } = useTnForm<TReportForm>()
  const queryClient = useQueryClient()

  const { data: organizations, isLoading: loadingOrgs } = useQuery(organizationQueries.list())

  // Set initial name when modal opens
  useEffect(() => {
    if (isOpen && initialName) {
      createFormFieldChangeHandler(form._name)(initialName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialName])

  const { mutate: createExpenseReport, isPending } = useMutation({
    mutationFn: (data: any) => expenseReportsApi.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: expenseReportQueries.all() })
      onSuccess(data)
      onClose()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid) return
    createExpenseReport(form.val)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('reportForm.createTitle')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-xl border border-border bg-surface p-4">
          <Input
            label={form._name.label}
            placeholder={form._name.placeholder}
            value={form._name.value}
            onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
            onBlur={() => form._name.validate()}
            autoFocus
          />
          <ErrorsList errors={form._name.errors} />

          <SelectWrapper
            label={form.organization.label}
            onBlur={() => form.organization.validate()}
          >
            <CustomSelect
              searchable
              options={
                organizations?.results?.map((org) => ({ label: org.name, value: org.id })) || []
              }
              values={form.organization.value ? [form.organization.value] : []}
              onChange={(values) => createFormFieldChangeHandler(form.organization)(values[0])}
              loading={loadingOrgs}
              placeholder={t('reportForm.selectOrganization')}
            />
          </SelectWrapper>
          <ErrorsList errors={form.organization.errors} />
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? t('common.creating') : t('reportForm.create')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export const CreateExpenseReportModal: React.FC<CreateExpenseReportModalProps> = (props) => {
  // Reset form when modal opens/closes by using key
  return (
    <FormProvider formClass={ReportForm} key={props.isOpen ? 'open' : 'closed'}>
      <CreateExpenseReportModalContent {...props} />
    </FormProvider>
  )
}
